import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export function GlobalSearch({ isAdmin }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setRes(null); return; }
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setRes(data);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <div className="flex items-center gap-2 px-3 py-2 panel rounded-sm">
        <Search size={15} className="text-[#8C867A]" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          placeholder={isAdmin ? "Search jobs, members, documents…" : "Search your jobs…"}
          className="bg-transparent outline-none text-sm w-full placeholder:text-[#5c5850] text-[#F5F3EF]"
        />
        {q && <X size={14} className="text-[#8C867A] cursor-pointer" onClick={() => { setQ(""); setRes(null); }} />}
      </div>
      {open && res && (
        <div className="absolute z-50 mt-1.5 w-full panel rounded-sm shadow-2xl max-h-[70vh] overflow-y-auto" data-testid="search-results">
          {(!res.jobs?.length && !res.members?.length && !res.documents?.length) && (
            <div className="px-4 py-4 text-sm text-[#8C867A]">No results found.</div>
          )}
          {res.jobs?.length > 0 && (
            <div className="p-2">
              <div className="eyebrow px-2 py-1">Trade Jobs</div>
              {res.jobs.map((j) => (
                <button key={j.id} data-testid={`search-job-${j.id}`} onClick={() => go(`/jobs/${j.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2 hover:bg-[#1A1A1E] rounded-sm text-left">
                  <div className="min-w-0">
                    <div className="font-mono text-xs brass-text">{j.job_number}</div>
                    <div className="text-sm text-[#E2DFD7] truncate">{j.title}</div>
                  </div>
                  <StatusBadge status={j.status} />
                </button>
              ))}
            </div>
          )}
          {res.members?.length > 0 && (
            <div className="p-2 border-t hairline">
              <div className="eyebrow px-2 py-1">Members</div>
              {res.members.map((m) => (
                <button key={m.id} onClick={() => go(`/members/${m.id}`)}
                  className="w-full flex items-center justify-between px-2 py-2 hover:bg-[#1A1A1E] rounded-sm text-left">
                  <div className="text-sm text-[#E2DFD7]">{m.name}</div>
                  <div className="text-xs text-[#8C867A]">{m.bench_number}</div>
                </button>
              ))}
            </div>
          )}
          {res.documents?.length > 0 && (
            <div className="p-2 border-t hairline">
              <div className="eyebrow px-2 py-1">Documents</div>
              {res.documents.map((d) => (
                <button key={d.id} onClick={() => go(`/documents`)}
                  className="w-full flex items-center justify-between px-2 py-2 hover:bg-[#1A1A1E] rounded-sm text-left">
                  <div className="text-sm text-[#E2DFD7]">{d.title}</div>
                  <div className="text-xs text-[#8C867A]">{d.category}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
